import { Action } from 'redux';
import { Dispatch } from 'react-redux';
import { actions } from 'react-redux-form';
import { SWODispatchViewModel } from '@models/ServiceWorkOrder';
import {
  PageViewModel,
  AppDispatcher,
  OptionsViewModel,
  AddressViewModel,
  blankAddressVM,
  blankOptionsVM,
  StatesViewModel
} from '@models/common.models';
import { swoPurchasingService } from '@services/SwoPurchasingService';
import { toast } from 'react-toastify';
import { ToastComponent, ToastType, DynamicSelectIdentifier } from '@common/components';
import * as React from 'react';
import { ToastOptions } from '@common/components/toast/toast';
import {
  PurchaseRequestViewModel,
  PurchaseRequestSearchViewModel,
  PurchasingRequestStatusesViewModel,
  Accessory,
  CollectionTypes,
  PurchaseRequestHvacViewModel,
  PurchaseType
} from '@models/PurchaseRequest';
import { requestAction, setFileListLoading, doAsync } from '@common/components/common.actions';
import { AppState } from 'src';
import { FileViewModel, DocumentViewModel } from '@models/Document';
import { documentService } from '@services/DocumentService';
import Constants from '@constants/Constants';
import FormNames from '@constants/FormNames.enum';
import { PurchasingState } from 'src/purchasing/purchasing.reducer';
import { utilityService } from '@services/UtilityService';
import { parsePhoneNumber } from '@common/parsers';
import { calculatePartsItemAmounts, calculatePurchaseRequestPartsTotals } from './purchase-parts.actions';

export enum PurchasingActionType {
  SearchPurchasingListStart = 'SearchPurchasingListStart',
  SearchPurchasingListSuccess = 'SearchPurchasingListSuccess',
  SearchPurchasingListFailure = 'SearchPurchasingListFailure',

  GetStatusCountStart = 'GetStatusCountStart',
  GetStatusCountSuccess = 'GetStatusCountSuccess',
  GetStatusCountFailure = 'GetStatusCountFailure',

  GetPurchaseRequestStart = 'GetPurchaseRequestStart',
  GetPurchaseRequestSuccess = 'GetPurchaseRequestSuccess',
  GetPurchaseRequestFailure = 'GetPurchaseRequestFailure',

  SetPurchaseRequest = 'SetPurchaseRequest',

  SetPurchaseRequestFiles = 'SetPurchaseRequestFiles',
  DeletePurchaseRequestFile = 'DeletePurchaseRequestFile',

  SetSupplier = 'SetSupplier',
  SetUSState = 'SetUSState',
  SetColor = 'SetColor',

  SetIsEditing = 'SetIsEditing',

  SetPurchasingOwnershipStart = 'SetPurchasingOwnershipStart',
  SetPurchasingOwnershipSuccess = 'SetPurchasingOwnershipSuccess',
  SetPurchasingOwnershipFailure = 'SetPurchasingOwnershipFailure',

  SetAccessories = 'SetAccessories',

  SetHvacItem = 'SetHvacItem',
  SetHvacSupplier = 'SetHvacSupplier',
  SetHvacSystemType = 'SetHvacSystemType',
  SetHvacPartModel = 'SetHvacPartModel',
  SetCollectionType = 'SetCollectionType',
  SetHvacDeliveryAddress = 'SetHvacDeliveryAddress',
  SetHvacVendorTechContactName = 'SetHvacVendorTechContactName',
  SetHvacVendorTechContactPhone = 'SetHvacVendorTechContactPhone',
  SetHvacPickupLocation = 'SetHvacPickupLocation',
  SetHvacEditItem = 'SetHvacEditItem',
  SetHvacSuppliers = 'SetHvacSuppliers',
  SetHvacSystemTypes = 'SetHvacSystemTypes',
  SetHvacPartModels = 'SetHvacPartModels',
  SetHvacUSStates = 'SetHvacUSStates',
  SetHvacVendorLocations = 'SetHvacVendorLocations',
  SetHvacLoading = 'SetHvacLoading',

  SetPartsSavings = 'SetPartsSavings'
}

/***** Search Purchasing List *****/
export interface SearchPurchasingListStartAction extends Action {
  readonly type: PurchasingActionType.SearchPurchasingListStart;
  readonly query: string | null;
  readonly includeClosed: boolean | undefined;
}
export interface SearchPurchasingListSuccessAction extends Action {
  readonly type: PurchasingActionType.SearchPurchasingListSuccess;
  readonly pageNumber: number;
  readonly pageSize: number;
  readonly orderBy: string;
  readonly includeClosed: boolean;
  readonly result: PageViewModel<PurchaseRequestSearchViewModel>;
}

export interface SearchPurchasingListFailureAction extends Action {
  readonly type: PurchasingActionType.SearchPurchasingListFailure;
  readonly error: string;
}

/***** Get Status Count *****/
export interface GetStatusCountStartAction extends Action {
  readonly type: PurchasingActionType.GetStatusCountStart;
}

export interface GetStatusCountSuccessAction extends Action {
  readonly type: PurchasingActionType.GetStatusCountSuccess;
  readonly statusCount: PurchasingRequestStatusesViewModel;
}

export interface GetStatusCountFailureAction extends Action {
  readonly type: PurchasingActionType.GetStatusCountFailure;
  readonly error: string;
}

/***** Get Purchase Request *****/
export interface GetPurchaseRequestStartAction extends Action {
  readonly type: PurchasingActionType.GetPurchaseRequestStart;
}

export interface GetPurchaseRequestSuccessAction extends Action {
  readonly type: PurchasingActionType.GetPurchaseRequestSuccess;
  readonly purchaseRequest: PurchaseRequestViewModel;
}

export interface GetPurchaseRequestFailureAction extends Action {
  readonly type: PurchasingActionType.GetPurchaseRequestFailure;
  readonly error: string;
}

export interface SetPurchaseRequestAction extends Action {
  readonly type: PurchasingActionType.SetPurchaseRequest;
  readonly purchaseRequest: PurchaseRequestViewModel;
}

export interface SetPurchaseRequestFilesAction extends Action {
  readonly type: PurchasingActionType.SetPurchaseRequestFiles;
  readonly documents: DocumentViewModel[];
}
export interface DeletePurchaseRequestFileAction extends Action {
  readonly type: PurchasingActionType.DeletePurchaseRequestFile;
  readonly fileId: number;
}

export interface SetSupplierAction extends Action {
  readonly type: PurchasingActionType.SetSupplier;
  readonly supplierId: number;
}

export interface SetUSStateAction extends Action {
  readonly type: PurchasingActionType.SetUSState;
  readonly stateAbbreviation: string;
}

export interface SetColorAction extends Action {
  readonly type: PurchasingActionType.SetColor;
  readonly color: string;
}

export interface SetIsEditingAction extends Action {
  readonly type: PurchasingActionType.SetIsEditing;
  readonly isEditing: boolean;
}

export interface SetAccessoriesAction extends Action {
  readonly type: PurchasingActionType.SetAccessories;
  readonly accessories: Array<Accessory>;
}

export interface SetHvacItemAction extends Action {
  readonly type: PurchasingActionType.SetHvacItem;
  readonly item: string;
}
export interface SetHvacSupplierAction extends Action {
  readonly type: PurchasingActionType.SetHvacSupplier;
  readonly selectedSupplier: OptionsViewModel;
}
export interface SetHvacSystemTypeAction extends Action {
  readonly type: PurchasingActionType.SetHvacSystemType;
  readonly selectedSystemType: OptionsViewModel;
}
export interface SetHvacPartModelAction extends Action {
  readonly type: PurchasingActionType.SetHvacPartModel;
  readonly selectedPartModel: OptionsViewModel;
}
export interface SetCollectionTypeAction extends Action {
  readonly type: PurchasingActionType.SetCollectionType;
  readonly selectedCollectionType: CollectionTypes;
}
export interface SetHvacDeliveryAddressAction extends Action {
  readonly type: PurchasingActionType.SetHvacDeliveryAddress;
  readonly deliveryAddress: AddressViewModel;
}
export interface SetHvacVendorTechContactNameAction extends Action {
  readonly type: PurchasingActionType.SetHvacVendorTechContactName;
  readonly vendorTechContactName: string;
}
export interface SetHvacVendorTechContactPhoneAction extends Action {
  readonly type: PurchasingActionType.SetHvacVendorTechContactPhone;
  readonly vendorTechContactPhone: string;
}
export interface SetHvacPickupLocationAction extends Action {
  readonly type: PurchasingActionType.SetHvacPickupLocation;
  readonly selectedPickupLocation: OptionsViewModel;
}
export interface SetHvacEditItemAction extends Action {
  readonly type: PurchasingActionType.SetHvacEditItem;
  readonly hvacPart: PurchaseRequestHvacViewModel;
  readonly index: number;
}
export interface SetHvacSuppliersAction extends Action {
  readonly type: PurchasingActionType.SetHvacSuppliers;
  readonly suppliers: Array<OptionsViewModel>;
}
export interface SetHvacSystemTypesAction extends Action {
  readonly type: PurchasingActionType.SetHvacSystemTypes;
  readonly systemTypes: Array<OptionsViewModel>;
}
export interface SetHvacPartModelsAction extends Action {
  readonly type: PurchasingActionType.SetHvacPartModels;
  readonly partModels: Array<OptionsViewModel>;
}
export interface SetHvacUSStatesAction extends Action {
  readonly type: PurchasingActionType.SetHvacUSStates;
  readonly usStates: Array<StatesViewModel>;
}
export interface SetHvacVendorLocationsAction extends Action {
  readonly type: PurchasingActionType.SetHvacVendorLocations;
  readonly vendorLocations: Array<OptionsViewModel>;
}
export interface SetHvacLoadingAction extends Action {
  readonly type: PurchasingActionType.SetHvacLoading;
  readonly isLoadingHvac: boolean;
}

/***** Search Purchasing List *****/
export function searchPurchasingList(
  query: string | null,
  pageSize: number,
  pageNumber: number,
  orderBy: string,
  orderDirection: string,
  includeClosed: boolean
) {
  return async (dispatch: Dispatch<SWODispatchViewModel>) => {
    dispatch(searchPurchasingListStart(query, includeClosed));
    try {
      let result: PageViewModel<PurchaseRequestSearchViewModel> = await swoPurchasingService.searchPurchaseRequests(
        query,
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        includeClosed
      );
      dispatch(searchPurchasingListSuccess(pageNumber, pageSize, orderBy, includeClosed, result!));
    } catch (e) {
      dispatch(searchPurchasingListFailure(e.toString()));
    }
  };
}
export function searchPurchasingListStart(
  query: string | null,
  includeClosed: boolean | undefined
): SearchPurchasingListStartAction {
  return {
    type: PurchasingActionType.SearchPurchasingListStart,
    query: query,
    includeClosed
  };
}
export function searchPurchasingListSuccess(
  pageNumber: number,
  pageSize: number,
  orderBy: string,
  includeClosed: boolean,
  result: PageViewModel<PurchaseRequestSearchViewModel>
): SearchPurchasingListSuccessAction {
  return {
    type: PurchasingActionType.SearchPurchasingListSuccess,
    pageNumber: pageNumber,
    pageSize: pageSize,
    orderBy: orderBy,
    includeClosed: includeClosed,
    result: result
  };
}
export function searchPurchasingListFailure(error: string): SearchPurchasingListFailureAction {
  toast.error(<ToastComponent type={ToastType.Error} message={error.toString()} />, ToastOptions.Error);

  return {
    type: PurchasingActionType.SearchPurchasingListFailure,
    error: error
  };
}

/***** Get Status Count *****/
export function getStatusCount() {
  return async (dispatch: Dispatch<PurchasingRequestStatusesViewModel>) => {
    dispatch(getStatusCountStart());
    try {
      let result: PurchasingRequestStatusesViewModel = await swoPurchasingService.getPurchaseRequestStatuses();
      dispatch(getStatusCountSuccess(result));
    } catch (e) {
      dispatch(getStatusCountFailure(e.toString()));
    }
  };
}
export function getStatusCountStart(): GetStatusCountStartAction {
  return {
    type: PurchasingActionType.GetStatusCountStart
  };
}
export function getStatusCountSuccess(statusCount: PurchasingRequestStatusesViewModel): GetStatusCountSuccessAction {
  return {
    type: PurchasingActionType.GetStatusCountSuccess,
    statusCount: statusCount
  };
}
export function getStatusCountFailure(error: string): GetStatusCountFailureAction {
  toast.error(<ToastComponent type={ToastType.Error} message={error.toString()} />, ToastOptions.Error);
  return {
    type: PurchasingActionType.GetStatusCountFailure,
    error: error
  };
}

/***** Get Purchase Request *****/

export function getPurchaseRequest(swoId: number) {
  return async (dispatch: Dispatch<PurchaseRequestViewModel>) => {
    dispatch(getPurchaseRequestStart());
    try {
      var purchaseRequest: PurchaseRequestViewModel = await swoPurchasingService.getPurchaseRequest(swoId);
      var prInfo = purchaseRequest.purchaseRequestInfo;
      if (prInfo) {
        switch (purchaseRequest.purchaseType && purchaseRequest.purchaseType.id) {
          case PurchaseType.HVACOptions:
          case PurchaseType.HVACPurchase:
          case PurchaseType.HVACRushPch:
            prInfo.hvacParts.forEach(part => {
              if (part.supplier === null) {
                part.supplier = blankOptionsVM;
              }
              if (part.systemType === null) {
                part.systemType = blankOptionsVM;
              }
              if (part.partModel === null) {
                part.partModel = blankOptionsVM;
              }
              if (part.collectionType === null) {
                part.collectionType = CollectionTypes.None;
              }
              if (part.deliveryAddress === null) {
                part.deliveryAddress = blankAddressVM;
              }
              if (part.pickupLocation === null) {
                part.pickupLocation = blankOptionsVM;
              }
              part.quantityPartCost = part.partCost * part.quantity;
            });
            dispatch(actions.change(FormNames.PurchaseHvacForm, prInfo));
            break;
          case PurchaseType.PartsSearch:
            if (prInfo.replacementCost === null) {
              prInfo.replacementCost = 0;
            }
            if (purchaseRequest.purchaseRequestInfo.deliveryAddress === null) {
              prInfo.deliveryAddress = blankAddressVM;
            }
            prInfo.parts.forEach(part => {
              if (part.brand === null) {
                part.brand = blankOptionsVM;
              }
              let partsAmounts = calculatePartsItemAmounts(part);
              part.quantityPartCost = partsAmounts.quantityPartCost;
              part.estTax = partsAmounts.estTax;
              part.total = partsAmounts.total;
            });
            let partsTotals = calculatePurchaseRequestPartsTotals(prInfo.parts, prInfo.replacementCost);
            prInfo.partCost = partsTotals.partCostTotal;
            prInfo.estTax = partsTotals.estTaxTotal;
            prInfo.restockingFee = partsTotals.restockFeeTotal;
            prInfo.delivery = partsTotals.deliveryFeeTotal;
            prInfo.installation = partsTotals.installationFeeTotal;
            prInfo.total = partsTotals.partsTotal;
            prInfo.savings = partsTotals.savings;
            dispatch(actions.change(FormNames.PurchasePartsForm, prInfo));
            break;
          default:
            dispatch(actions.change(FormNames.PurchaseApplianceInfoForm, prInfo));
            break;
        }
      }
      return dispatch(getPurchaseRequestSuccess(purchaseRequest));
    } catch (e) {
      return dispatch(getPurchaseRequestFailure(e.toString()));
    }
  };
}
export function getPurchaseRequestStart(): GetPurchaseRequestStartAction {
  return {
    type: PurchasingActionType.GetPurchaseRequestStart
  };
}
export function getPurchaseRequestSuccess(purchaseRequest: PurchaseRequestViewModel): GetPurchaseRequestSuccessAction {
  return {
    type: PurchasingActionType.GetPurchaseRequestSuccess,
    purchaseRequest: purchaseRequest
  };
}
export function getPurchaseRequestFailure(error: string): GetPurchaseRequestFailureAction {
  toast.error(<ToastComponent type={ToastType.Error} message={error.toString()} />, ToastOptions.Error);

  return {
    type: PurchasingActionType.GetPurchaseRequestFailure,
    error: error
  };
}

export function createPurchaseRequest(purchaseRequest: PurchaseRequestViewModel) {
  return async (dispatch: Dispatch<AppState>, getState: () => AppState) => {
    return dispatch(
      requestAction(() => swoPurchasingService.createPurchaseRequest(purchaseRequest), setPurchaseRequest, {
        hasToast: true,
        toastSuccessMessage: 'Purchase Request Created Successfully.'
      })
    );
  };
}

export function setPurchaseRequest(response: PurchaseRequestViewModel): SetPurchaseRequestAction {
  return {
    type: PurchasingActionType.SetPurchaseRequest,
    purchaseRequest: response
  };
}

/***** Save Purchase Request *****/
export function savePurchaseRequest(id: number, purchaseRequest: PurchaseRequestViewModel) {
  return async (dispatch: Dispatch<AppState>, getState: () => AppState) => {
    if (!purchaseRequest || !purchaseRequest.purchaseRequestInfo) {
      toast.error(
        <ToastComponent type={ToastType.Error} message={'Error submitting purchase request.'} />,
        ToastOptions.Error
      );
      return Promise.reject('');
    }
    let appState = getState();
    let dynamicOptions = appState.dynamicSelect.options[DynamicSelectIdentifier.Colors];
    let color = dynamicOptions.find(x => x.id === +purchaseRequest.purchaseRequestInfo.color.id);
    if (!color) {
      color = purchaseRequest.purchaseRequestInfo.color;
    }
    let pr: PurchaseRequestViewModel = {
      ...purchaseRequest,
      purchaseRequestInfo: { ...purchaseRequest.purchaseRequestInfo, color: color }
    };
    return dispatch(
      requestAction(() => swoPurchasingService.savePurchaseRequest(id, pr), setPurchaseRequest, {
        hasToast: true,
        toastSuccessMessage: 'Purchase Request Submitted Successfully.'
      })
    );
  };
}

export function calculateTotal(purchaseRequest: PurchaseRequestViewModel) {
  return async (dispatch: Dispatch<AppState>, getState: () => AppState) => {
    const prevState = getState();
    const purchasingState = prevState.purchasing;
    const statePR = purchasingState.purchaseRequest;
    const statePRInfo = statePR ? statePR.purchaseRequestInfo : undefined;

    const formPRInfo = purchaseRequest.purchaseRequestInfo;

    const formPartCost = Math.round((+formPRInfo.partCost || 0) * 100) / 100;
    const estTax = Math.round((+formPRInfo.partCost * 0.095 || 0) * 100) / 100;
    const restockingFee = formPRInfo.partCost ? Math.round((+formPRInfo.partCost * 0.35 + 55 || 0) * 100) / 100 : 0;

    let delivery = Math.round((+formPRInfo.delivery || 0) * 100) / 100;
    let installation = Math.round((+formPRInfo.installation || 0) * 100) / 100;
    let disposalFee = Math.round((+formPRInfo.disposalFee || 0) * 100) / 100;

    const formSupplier = formPRInfo.supplier;
    if (formSupplier) {
      const isLowes = +formSupplier.id === Constants.LOWES_ID;
      delivery = isLowes ? 0 : 30;
      installation = isLowes ? 0 : 75;
      disposalFee = isLowes ? 0 : 50;
    }

    const accessories = purchasingState.accessories;
    const accessoriesCost =
      accessories.length && formSupplier && +formSupplier.id === Constants.LOWES_ID
        ? Math.round(accessories.map(acc => +acc.supplierPrice).reduce((a: number, b: number) => a + b))
        : 0;

    const total =
      Math.round(
        (formPartCost + estTax + restockingFee + delivery + installation + disposalFee + accessoriesCost) * 100
      ) / 100;

    const formCalculatedCIL = formPRInfo.cilOfferOnly ? +total : Math.round(+total * 0.8 * 100) / 100;
    // const stateCalculatedCIL = statePRInfo ? statePRInfo.calculatedCIL : undefined;

    const statePartCost = statePRInfo ? +statePRInfo.partCost : 0;
    // const formCILTotal = formPRInfo.cilTotal.toString().trim();

    let cilOfferReason = formPRInfo.cilOfferReason;
    if (statePR && statePRInfo && formPRInfo.cilOfferOnly !== statePRInfo.cilOfferOnly) {
      cilOfferReason = blankOptionsVM;
    }

    let cilTotal = 0;
    if (formPRInfo.cilOfferOnly) {
      if (+formPRInfo.cilTotal > 0) {
        cilTotal = formPRInfo.cilTotal;
      } else if (
        statePR &&
        statePRInfo &&
        +formPRInfo.partCost > 0 &&
        (formPartCost !== statePartCost || formPRInfo.cilOfferOnly !== statePRInfo.cilOfferOnly)
      ) {
        cilTotal = formCalculatedCIL;
      }
    }

    let prVM: PurchaseRequestViewModel = {
      ...purchaseRequest,
      purchaseRequestInfo: {
        ...purchaseRequest.purchaseRequestInfo,
        partCost: formPartCost,
        estTax: estTax,
        restockingFee: restockingFee,
        delivery: delivery,
        installation: installation,
        disposalFee: disposalFee,
        total: total,
        calculatedCIL: formCalculatedCIL,
        cilTotal: cilTotal,
        cilOfferReason: cilOfferReason
      }
    };

    await dispatch(
      actions.change(FormNames.PurchaseApplianceInfoForm, {
        ...prVM.purchaseRequestInfo
      })
    );
    if (+cilTotal > 0) {
      dispatch(actions.blur(FormNames.PurchaseApplianceInfoForm + '.cilTotal'));
    }

    dispatch(setPurchaseRequest(prVM));
  };
}

/********** Get Purchase Request Files **********/
export function getPurchaseRequestFiles(id: number) {
  return async (dispatch: Dispatch<AppState>, getState: () => AppState) => {
    return dispatch(requestAction(() => documentService.getPurchaseRequestFiles(id), setPurchaseRequestFiles));
  };
}
function setPurchaseRequestFiles(documentVMs: DocumentViewModel[]): SetPurchaseRequestFilesAction {
  return {
    type: PurchasingActionType.SetPurchaseRequestFiles,
    documents: documentVMs
  };
}

/********** Save Purchase Request Files **********/
export function savePurchaseRequestFiles(id: number, fileVMs: FileViewModel[]) {
  return async (dispatch: Dispatch<AppState>, getState: () => AppState) => {
    return dispatch(
      requestAction(() => documentService.saveFiles(fileVMs, undefined, undefined, id), setPurchaseRequestFiles, {
        hasToast: true,
        toastSuccessMessage: 'Purchase Request Files Saved Successfully.',
        loadingAction: setFileListLoading
      })
    ).then(() => dispatch(getPurchaseRequestFiles(id)));
  };
}

/********** Remove Purchase Request File **********/
export function removePurchaseRequestFile(id: number, fileId: number) {
  return async (dispatch: Dispatch<AppState>, getState: () => AppState) => {
    return dispatch(
      requestAction(() => documentService.removeFile(fileId), deletePurchaseRequestFile, {
        hasToast: true,
        toastSuccessMessage: 'Purchase Request File Removed Successfully.',
        loadingAction: setFileListLoading
      })
    ).then(() => dispatch(getPurchaseRequestFiles(id)));
  };
}
function deletePurchaseRequestFile(fileId: number): DeletePurchaseRequestFileAction {
  return {
    type: PurchasingActionType.DeletePurchaseRequestFile,
    fileId: fileId
  };
}

function setSupplier(supplierId: number): SetSupplierAction {
  return {
    type: PurchasingActionType.SetSupplier,
    supplierId: supplierId
  };
}
export function selectSupplier(supplierId: number) {
  return (dispatch: Dispatch<AppState>, getState: () => AppState) => {
    if (+supplierId === Constants.LOWES_ID) {
      const prevState = getState();
      dispatch(
        actions.change(FormNames.PurchaseApplianceInfoForm, {
          ...prevState.purchaseApplianceInfoForm,
          installation: 0,
          disposalFee: 0,
          delivery: 0
        })
      );
    }

    dispatch(setSupplier(supplierId));
  };
}

function setUSState(stateAbbr: string): SetUSStateAction {
  return {
    type: PurchasingActionType.SetUSState,
    stateAbbreviation: stateAbbr
  };
}
export function selectUSState(stateAbreviation: string) {
  return (dispatch: Dispatch<AppState>, getState: () => AppState) => {
    dispatch(setUSState(stateAbreviation));
  };
}

function setColor(color: string): SetColorAction {
  return {
    type: PurchasingActionType.SetColor,
    color: color
  };
}
export function selectColor(color: string) {
  return (dispatch: Dispatch<AppState>, getState: () => AppState) => {
    dispatch(setColor(color));
  };
}

function setIsEditing(isEditing: boolean): SetIsEditingAction {
  return {
    type: PurchasingActionType.SetIsEditing,
    isEditing: isEditing
  };
}
export function toggleIsEditing(isEditing: boolean) {
  return (dispatch: Dispatch<AppState>, getState: () => AppState) => {
    dispatch(setIsEditing(isEditing));
  };
}

/***** Reopen Purchase Request *****/
export function reopenPurchaseRequest(id: number) {
  return async (dispatch: Dispatch<AppState>, getState: () => AppState) => {
    return dispatch(
      requestAction(() => swoPurchasingService.reopenPurchaseRequest(id), setPurchaseRequest, {
        hasToast: true,
        toastSuccessMessage: 'Successfully Reopened Purchase Request',
        overrideFailMessage: 'Unable to Reopen Purchase Request'
      })
    ).then(() => dispatch(getPurchaseRequest(id)));
  };
}

/***** Complete Purchase Request *****/
export function completePurchaseRequest(id: number) {
  return async (dispatch: Dispatch<AppState>, getState: () => AppState) => {
    return dispatch(
      requestAction(() => swoPurchasingService.completePurchaseRequest(id), setPurchaseRequest, {
        hasToast: true,
        toastSuccessMessage: 'Successfully Closed Purchase Request',
        overrideFailMessage: 'Unable to Close Purchase Request'
      })
    ).then(() => dispatch(getPurchaseRequest(id)));
  };
}

/**** Set Purchasing Ownership ****/
export interface SetPurchasingOwnershipStartAction extends Action {
  readonly type: PurchasingActionType.SetPurchasingOwnershipStart;
}

export interface SetPurchasingOwnershipSuccessAction extends Action {
  readonly type: PurchasingActionType.SetPurchasingOwnershipSuccess;
  readonly purchaseRequest: PurchaseRequestSearchViewModel;
  readonly release?: boolean;
}

export interface SetPurchasingOwnershipFailureAction extends Action {
  readonly type: PurchasingActionType.SetPurchasingOwnershipFailure;
  readonly error: string;
}
export function changePurchasingOwnership(id: number, release?: boolean) {
  return async (dispatch: Dispatch<PurchasingActionType>) => {
    dispatch(setPurchasingOwnershipStart());
    try {
      const purchaseRequest = await swoPurchasingService.changePurchasingOwnership(id, release);
      return dispatch(setPurchasingOwnershipSuccess(purchaseRequest, release));
    } catch (e) {
      return dispatch(setPurchasingOwnershipFailure(e));
    }
  };
}

export function setPurchasingOwnershipStart(): SetPurchasingOwnershipStartAction {
  return {
    type: PurchasingActionType.SetPurchasingOwnershipStart
  };
}

export function setPurchasingOwnershipSuccess(
  purchaseRequest: PurchaseRequestSearchViewModel,
  release?: boolean
): SetPurchasingOwnershipSuccessAction {
  return {
    type: PurchasingActionType.SetPurchasingOwnershipSuccess,
    purchaseRequest: purchaseRequest,
    release: release
  };
}

export function setPurchasingOwnershipFailure(error: string): SetPurchasingOwnershipFailureAction {
  toast.error(<ToastComponent type={ToastType.Error} message={error.toString()} />, ToastOptions.Error);
  return {
    type: PurchasingActionType.SetPurchasingOwnershipFailure,
    error: error
  };
}

export function getAccessories(cviId: number) {
  return async (dispatch: AppDispatcher, getState: () => AppState) => {
    return dispatch(
      requestAction(() => swoPurchasingService.getAccessories(cviId), setAccessories, {
        hasToast: false,
        overrideFailMessage: 'Unable to fetch Accessories'
      })
    );
  };
}

export function setAccessories(accessories: Accessory[]): SetAccessoriesAction {
  return {
    type: PurchasingActionType.SetAccessories,
    accessories: accessories
  };
}

/***** HVAC Item *****/
export function setHvacItem(item: string): SetHvacItemAction {
  return {
    type: PurchasingActionType.SetHvacItem,
    item: item
  };
}

/***** HVAC Supplier *****/
function setHvacSupplier(selectedSupplier: OptionsViewModel): SetHvacSupplierAction {
  return {
    type: PurchasingActionType.SetHvacSupplier,
    selectedSupplier: selectedSupplier
  };
}
export function selectHvacSupplier(selectedSupplierId: number) {
  return (dispatch: Dispatch<AppState>, getState: () => AppState) => {
    const prevState = getState();
    const hvavSuppliers = prevState.purchasing.hvacSuppliers;
    var selectedSupplier = hvavSuppliers.find(x => x.id === selectedSupplierId) || blankOptionsVM;
    dispatch(setHvacSupplier(selectedSupplier));
    dispatch(actions.change(FormNames.PurchaseHvacItemForm + '.systemType', blankOptionsVM));
    dispatch(actions.change(FormNames.PurchaseHvacItemForm + '.partModel', blankOptionsVM));
    dispatch(actions.change(FormNames.PurchaseHvacItemForm + '.pickupLocation', blankOptionsVM));
  };
}

/***** HVAC System Type *****/
function setHvacSystemType(selectedSystemType: OptionsViewModel): SetHvacSystemTypeAction {
  return {
    type: PurchasingActionType.SetHvacSystemType,
    selectedSystemType: selectedSystemType
  };
}
export function selectHvacSystemType(selectedSystemTypeId: number) {
  return (dispatch: Dispatch<AppState>, getState: () => AppState) => {
    const prevState = getState();
    const hvacSystemTypes = prevState.purchasing.hvacSystemTypes;
    var selectedSystemType = hvacSystemTypes.find(x => x.id === selectedSystemTypeId) || blankOptionsVM;
    dispatch(setHvacSystemType(selectedSystemType));
    dispatch(actions.change(FormNames.PurchaseHvacItemForm + '.partModel', blankOptionsVM));
  };
}

/***** HVAC Part Model *****/
function setHvacPartModel(selectedPartModel: OptionsViewModel): SetHvacPartModelAction {
  return {
    type: PurchasingActionType.SetHvacPartModel,
    selectedPartModel: selectedPartModel
  };
}
export function selectHvacPartModel(selectedPartModelId: number) {
  return (dispatch: Dispatch<AppState>, getState: () => AppState) => {
    const prevState = getState();
    const hvacParts = prevState.purchasing.hvacPartModels;
    var selectedPartModel = hvacParts.find(x => x.id === selectedPartModelId) || blankOptionsVM;
    dispatch(setHvacPartModel(selectedPartModel));
  };
}

/***** Collection Type *****/
function setCollectionType(selectedCollectionType: CollectionTypes): SetCollectionTypeAction {
  return {
    type: PurchasingActionType.SetCollectionType,
    selectedCollectionType: selectedCollectionType
  };
}
export function selectCollectionType(selectedCollectionType: CollectionTypes) {
  return (dispatch: Dispatch<AppState>, getState: () => AppState) => {
    dispatch(setCollectionType(selectedCollectionType));
    switch (selectedCollectionType) {
      case CollectionTypes.Delivered:
        dispatch(actions.change(FormNames.PurchaseHvacItemForm + '.pickupLocation', blankOptionsVM));
        break;
      case CollectionTypes.Pickup:
        dispatch(actions.change(FormNames.PurchaseHvacItemForm + '.deliveryAddress', blankAddressVM));
        dispatch(actions.change(FormNames.PurchaseHvacItemForm + '.vendorTechContactName', ''));
        dispatch(actions.change(FormNames.PurchaseHvacItemForm + '.vendorTechContactPhone', ''));
        dispatch(actions.change(FormNames.PurchaseHvacItemForm + '.deliveryFee', 0.0));
        break;
      default:
        dispatch(actions.change(FormNames.PurchaseHvacItemForm + '.deliveryAddress', blankAddressVM));
        dispatch(actions.change(FormNames.PurchaseHvacItemForm + '.vendorTechContactName', ''));
        dispatch(actions.change(FormNames.PurchaseHvacItemForm + '.vendorTechContactPhone', ''));
        dispatch(actions.change(FormNames.PurchaseHvacItemForm + '.deliveryFee', 0.0));
        dispatch(actions.change(FormNames.PurchaseHvacItemForm + '.pickupLocation', blankOptionsVM));
        break;
    }
  };
}

/***** HVAC Delivery Address *****/
function setHvacDeliveryAddress(deliveryAddress: AddressViewModel): SetHvacDeliveryAddressAction {
  return {
    type: PurchasingActionType.SetHvacDeliveryAddress,
    deliveryAddress: deliveryAddress
  };
}
export function updateHvacDeliveryAddress(deliveryAddress: AddressViewModel) {
  return (dispatch: Dispatch<AppState>, getState: () => AppState) => {
    dispatch(setHvacDeliveryAddress(deliveryAddress));
  };
}

/***** HVAC Vendor Tech Contact Name *****/
export function setHvacVendorTechContactName(vendorTechContactName: string): SetHvacVendorTechContactNameAction {
  return {
    type: PurchasingActionType.SetHvacVendorTechContactName,
    vendorTechContactName: vendorTechContactName
  };
}

/***** HVAC Vendor Tech Contact Phone *****/
export function setHvacVendorTechContactPhone(vendorTechContactPhone: string): SetHvacVendorTechContactPhoneAction {
  return {
    type: PurchasingActionType.SetHvacVendorTechContactPhone,
    vendorTechContactPhone: vendorTechContactPhone
  };
}

/***** HVAC Pickup Location *****/
function setHvacPickupLocation(selectedPickupLocation: OptionsViewModel): SetHvacPickupLocationAction {
  return {
    type: PurchasingActionType.SetHvacPickupLocation,
    selectedPickupLocation: selectedPickupLocation
  };
}
export function selectHvacPickupLocation(selectedPickupLocationId: number) {
  return (dispatch: Dispatch<AppState>, getState: () => AppState) => {
    const prevState = getState();
    const hvacVendorLocations = prevState.purchasing.hvacVendorLocations;
    var selectedPickupLocation = hvacVendorLocations.find(x => x.id === selectedPickupLocationId) || blankOptionsVM;
    if (selectedPickupLocation) {
      dispatch(setHvacPickupLocation(selectedPickupLocation));
    }
  };
}

/***** Hvac Edit Item *****/
export function setHvacEditItem(hvacPart: PurchaseRequestHvacViewModel, hvacPartIndex: number): SetHvacEditItemAction {
  return {
    type: PurchasingActionType.SetHvacEditItem,
    hvacPart: hvacPart,
    index: hvacPartIndex
  };
}

function setHvacItemForm(hvacPart: PurchaseRequestHvacViewModel, hvacPartIndex: number) {
  return (dispatch: Dispatch<AppState>, getState: () => AppState) => {
    return dispatch(doAsync(setHvacEditItem(hvacPart, hvacPartIndex))).then(() => {
      let vendorTechContactPhone = hvacPart.vendorTechContactPhone
        ? parsePhoneNumber(hvacPart.vendorTechContactPhone)
        : hvacPart.vendorTechContactPhone;
      let collectionTypeString = hvacPart.collectionType
        ? typeof hvacPart.collectionType === 'number'
          ? JSON.stringify(hvacPart.collectionType)
          : hvacPart.collectionType
        : undefined;
      let prHvacPart = {
        ...hvacPart,
        collectionType: collectionTypeString,
        vendorTechContactPhone: vendorTechContactPhone
      } as PurchaseRequestHvacViewModel;
      dispatch(actions.change(FormNames.PurchaseHvacItemForm, prHvacPart));
    });
  };
}

export function editHvacItem(hvacPart: PurchaseRequestHvacViewModel, hvacPartIndex: number) {
  return (dispatch: Dispatch<AppState>, getState: () => AppState) => {
    let supplierId = hvacPart && hvacPart.supplier ? hvacPart.supplier!.id : 0;
    let systemTypeId = hvacPart && hvacPart.systemType ? hvacPart.systemType!.id : 0;
    return Promise.all([
      dispatch(getHvacSystemTypes(supplierId)),
      dispatch(getHvacVendorLocations(supplierId)),
      dispatch(getHvacPartModels(systemTypeId))
    ]).then(() => {
      dispatch(setHvacItemForm(hvacPart, hvacPartIndex));
    });
  };
}

/***** Hvac Calculate Item *****/
export function calculateHvacItem(hvacPart: PurchaseRequestHvacViewModel) {
  return (dispatch: Dispatch<AppState>, getState: () => AppState) => {
    const prevState = getState();
    const purchasingState = prevState.purchasing;
    const hvacPartIndex = purchasingState.hvacPartIndex;
    var statePart = purchasingState.hvacPart;
    var formPart = hvacPart;
    if (formPart && statePart) {
      let quantity = Math.round((+formPart.quantity! || 1) * 100) / 100;
      let partCost = Math.round((+formPart.partCost! || 0) * 100) / 100;
      let quantityPartCost = partCost * quantity;
      let estTax = Math.round((quantityPartCost * 0.095 || 0) * 100) / 100;
      let restockFee = Math.round((+formPart.restockFee! || 0) * 100) / 100;
      let deliveryFee = Math.round((+formPart.deliveryFee! || 0) * 100) / 100;
      let installationFee = Math.round((+formPart.installationFee! || 0) * 100) / 100;
      let disposalFee = Math.round((+formPart.disposalFee! || 0) * 100) / 100;
      let total = quantityPartCost + estTax + restockFee + deliveryFee + installationFee + disposalFee;

      var newHvacPart: PurchaseRequestHvacViewModel = {
        ...statePart,
        quantity: quantity,
        partCost: partCost,
        estTax: estTax,
        restockFee: restockFee,
        deliveryFee: deliveryFee,
        installationFee: installationFee,
        disposalFee: disposalFee,
        quantityPartCost: quantityPartCost,
        total: total
      };

      dispatch(setHvacItemForm(newHvacPart, hvacPartIndex));

      return Promise.resolve();
    }
    return Promise.reject('');
  };
}

/***** Hvac Remove Item *****/
export function removeHvacItem(hvacPartIndex: number) {
  return (dispatch: Dispatch<AppState>, getState: () => AppState) => {
    dispatch(toggleHvacLoading(true));
    const prevState = getState();
    const purchasingState = prevState.purchasing;
    const purchaseRequest = purchasingState.purchaseRequest;
    if (purchaseRequest && purchaseRequest.purchaseRequestInfo) {
      const stateHvacParts = purchaseRequest.purchaseRequestInfo.hvacParts;
      if (stateHvacParts) {
        var newArray = stateHvacParts.slice();
        if (hvacPartIndex >= 0) {
          newArray.splice(hvacPartIndex, 1);

          dispatch(calculatePurchaseRequestHvac(newArray, purchaseRequest)).then(() => {
            const pr = getState().purchasing.purchaseRequest;
            if (pr) {
              dispatch(savePurchaseRequestHvac(pr.id, pr)).then(() => {
                dispatch(getPurchaseRequest(pr.id)).then(() => {
                  dispatch(toggleHvacLoading(false));
                });
              });
            }
          });

          return Promise.resolve();
        }
      }
    }
    return Promise.reject('');
  };
}

/***** Hvac Submit Item *****/
export function submitHvacItem(hvacPart: PurchaseRequestHvacViewModel) {
  return (dispatch: Dispatch<AppState>, getState: () => AppState) => {
    dispatch(toggleHvacLoading(true));
    const purchasingState = getState().purchasing;
    const purchaseRequest = purchasingState.purchaseRequest;
    const hvacPartIndex = purchasingState.hvacPartIndex;
    if (purchaseRequest && purchaseRequest.purchaseRequestInfo) {
      const stateHvacParts = purchaseRequest.purchaseRequestInfo.hvacParts;
      if (stateHvacParts) {
        var newArray = stateHvacParts.slice();

        let prHvacPart = {
          ...hvacPart,
          quantity: +hvacPart.quantity,
          partCost: +hvacPart.partCost,
          restockFee: +hvacPart.restockFee,
          deliveryFee: +hvacPart.deliveryFee,
          installationFee: +hvacPart.installationFee,
          disposalFee: +hvacPart.disposalFee
        } as PurchaseRequestHvacViewModel;

        hvacPartIndex < 0 ? newArray.push(prHvacPart) : newArray.splice(hvacPartIndex, 1, prHvacPart);

        dispatch(calculatePurchaseRequestHvac(newArray, purchaseRequest)).then(() => {
          const pr = getState().purchasing.purchaseRequest;
          if (pr) {
            dispatch(savePurchaseRequestHvac(pr.id, pr)).then(() => {
              dispatch(getPurchaseRequest(pr.id)).then(() => {
                dispatch(toggleHvacLoading(false));
              });
            });
          }
        });

        return Promise.resolve();
      }
    }
    return Promise.reject('');
  };
}

function calculatePurchaseRequestHvac(
  newArray: PurchaseRequestHvacViewModel[],
  purchaseRequest: PurchaseRequestViewModel
) {
  return async (dispatch: Dispatch<AppState>, getState: () => AppState) => {
    let partCostTotal = newArray.map(x => +x.quantityPartCost! || 0).reduce((a, b) => a + b, 0);
    let estTaxTotal = newArray.map(x => +x.estTax! || 0).reduce((a, b) => a + b, 0);
    let restockFeeTotal = newArray.map(x => +x.restockFee! || 0).reduce((a, b) => a + b, 0);
    let deliveryFeeTotal = newArray.map(x => +x.deliveryFee! || 0).reduce((a, b) => a + b, 0);
    let installationFeeTotal = newArray.map(x => +x.installationFee! || 0).reduce((a, b) => a + b, 0);
    let disposalFeeTotal = newArray.map(x => +x.disposalFee! || 0).reduce((a, b) => a + b, 0);
    let partsTotal = newArray.map(x => +x.total! || 0).reduce((a, b) => a + b, 0);

    let prVM: PurchaseRequestViewModel = {
      ...purchaseRequest,
      purchaseRequestInfo: {
        ...purchaseRequest.purchaseRequestInfo,
        hvacParts: newArray,
        partCost: partCostTotal,
        estTax: estTaxTotal,
        restockingFee: restockFeeTotal,
        delivery: deliveryFeeTotal,
        installation: installationFeeTotal,
        disposalFee: disposalFeeTotal,
        total: partsTotal
      }
    };

    dispatch(actions.change(FormNames.PurchaseHvacForm, { ...prVM.purchaseRequestInfo }));

    dispatch(setPurchaseRequest(prVM));
  };
}

/***** Save Purchase Request Hvac *****/
export function savePurchaseRequestHvac(id: number, purchaseRequest: PurchaseRequestViewModel) {
  return async (dispatch: Dispatch<AppState>, getState: () => AppState) => {
    let pr = { ...purchaseRequest } as PurchaseRequestViewModel;
    if (purchaseRequest.purchaseRequestInfo && purchaseRequest.purchaseRequestInfo.hvacParts) {
      let hvacParts = purchaseRequest.purchaseRequestInfo.hvacParts.slice();
      purchaseRequest.purchaseRequestInfo.hvacParts.forEach((value, index) => {
        let part = { ...value } as PurchaseRequestHvacViewModel;
        if (value.supplier && value.supplier.id <= 0) {
          part.supplier = undefined;
        }
        if (value.systemType && value.systemType!.id <= 0) {
          part.systemType = undefined;
        }
        if (value.partModel && value.partModel!.id <= 0) {
          part.partModel = undefined;
        }
        if (value.deliveryAddress && value.deliveryAddress!.line1.length <= 0) {
          part.deliveryAddress = undefined;
        }
        if (value.pickupLocation && value.pickupLocation!.id <= 0) {
          part.pickupLocation = undefined;
        }
        hvacParts.splice(index, 1, part);
      });
      pr.purchaseRequestInfo.hvacParts = hvacParts;
    }
    return dispatch(
      requestAction(() => swoPurchasingService.savePurchaseRequestHvac(id, pr), setPurchaseRequest, {
        hasToast: true,
        toastSuccessMessage: 'HVAC Purchase Request Modified Successfully.'
      })
    );
  };
}

/***** Get HVAC Suppliers *****/
export function setHvacSuppliers(suppliers: Array<OptionsViewModel>): SetHvacSuppliersAction {
  return {
    type: PurchasingActionType.SetHvacSuppliers,
    suppliers: suppliers
  };
}
export function getHvacSuppliers() {
  return async (dispatch: Dispatch<PurchasingState>) => {
    return dispatch(requestAction(() => swoPurchasingService.getSuppliers(true), setHvacSuppliers));
  };
}

/***** Get HVAC System Types *****/
export function setHvacSystemTypes(systemTypes: Array<OptionsViewModel>): SetHvacSystemTypesAction {
  return {
    type: PurchasingActionType.SetHvacSystemTypes,
    systemTypes: systemTypes
  };
}
export function getHvacSystemTypes(supplierId: number) {
  return async (dispatch: Dispatch<PurchasingState>) => {
    return dispatch(requestAction(() => swoPurchasingService.getHvacSystemTypes(supplierId), setHvacSystemTypes));
  };
}

/***** Get HVAC Part Models *****/
export function setHvacPartModels(partModels: Array<OptionsViewModel>): SetHvacPartModelsAction {
  return {
    type: PurchasingActionType.SetHvacPartModels,
    partModels: partModels
  };
}
export function getHvacPartModels(systemTypeId: number) {
  return async (dispatch: Dispatch<PurchasingState>) => {
    return dispatch(requestAction(() => swoPurchasingService.getHvacParts(systemTypeId), setHvacPartModels));
  };
}

/***** Get HVAC USStates *****/
export function setHvacUSStates(usStates: Array<StatesViewModel>): SetHvacUSStatesAction {
  return {
    type: PurchasingActionType.SetHvacUSStates,
    usStates: usStates
  };
}
export function getHvacUSStates() {
  return async (dispatch: Dispatch<PurchasingState>) => {
    return dispatch(requestAction(() => utilityService.getStates(), setHvacUSStates));
  };
}

/***** Get HVAC Vendor Locations *****/
export function setHvacVendorLocations(vendorLocations: Array<OptionsViewModel>): SetHvacVendorLocationsAction {
  return {
    type: PurchasingActionType.SetHvacVendorLocations,
    vendorLocations: vendorLocations
  };
}
export function getHvacVendorLocations(supplierId: number) {
  return async (dispatch: Dispatch<PurchasingState>) => {
    return dispatch(
      requestAction(() => swoPurchasingService.getHvacVendorLocations(supplierId), setHvacVendorLocations)
    );
  };
}

/***** Hvac Loading *****/
function setHvacLoading(isLoadingHvac: boolean): SetHvacLoadingAction {
  return {
    type: PurchasingActionType.SetHvacLoading,
    isLoadingHvac: isLoadingHvac
  };
}
export function toggleHvacLoading(isLoadingHvac: boolean) {
  return (dispatch: Dispatch<AppState>, getState: () => AppState) => {
    dispatch(setHvacLoading(isLoadingHvac));
    return Promise.resolve();
  };
}

export interface SetPartsSavingsAction extends Action {
  readonly type: PurchasingActionType.SetPartsSavings;
  readonly replacementCost: number;
  readonly savings: number;
}
export function setPartsSavings(replacementCost: number, savings: number): SetPartsSavingsAction {
  return {
    type: PurchasingActionType.SetPartsSavings,
    replacementCost: replacementCost,
    savings: savings
  };
}
export function calculateSavings(replacementCost: number) {
  return async (dispatch: AppDispatcher, getState: () => AppState) => {
    const pr = getState().purchasing.purchaseRequest;
    let savings = pr && pr.purchaseRequestInfo ? pr.purchaseRequestInfo.total - replacementCost : 0;
    await dispatch(setPartsSavings(replacementCost, savings));
  };
}



// WEBPACK FOOTER //
// ./src/purchasing/purchasing.actions.tsx